// Apps launcher — search-focus dropdown + app navigation.
//
// Replaces the home AppsWidget card (removed 2026-06-06 per Nika): the
// same app list now appears as a @symbo.ls/dropdown panel anchored to
// the global search bar whenever the (empty) search input gains focus.
// Navbar apps own their URLs; other app opens use PageSlot.

// The four canvas views (Frontend · Infra · Tasks · Agents) — single source of
// truth is CanvasViewNav's VIEW_NAV (the left-rail switcher). The apps launcher
// surfaces them as their own VIEWS section so the navbar dropdown offers the
// same view-switch the sidebar does, without re-typing the routes.
// ⚠ VIEW_NAV must stay a DEFERRED read (getViewNav(), never a module-scope
// `const { VIEW_NAV } = _wsShared.components`): this module loads INSIDE
// the shared-barrel init cycle — same class as workspaceProjects.js/
// pageContext.js/featureFlags.js's fix note — and a module-scope
// destructure here crashed the PRODUCTION BUILD itself ("Cannot
// destructure property 'VIEW_NAV' of '...default.components' as it is
// undefined", smbls push, tickets/execution-board.md §1).
import _wsShared from '@symbo-ls/workspace-shared'
import { shellStripWorkspacePrefix } from './shellSharedDelegates.js'
const shellNavItemVisible = (...args) => _wsShared.functions.navItemVisible(...args)
const shellCleanHref = (...args) => _wsShared.functions.cleanHref(...args)
const shellSlotPaneKeyFor = (...args) => _wsShared.functions.slotPaneKeyFor(...args)
const shellIsModuleCanvasRoute = (...args) => _wsShared.functions.isModuleCanvasRoute(...args)
const getViewNav = () => _wsShared.components.VIEW_NAV
import { adminAreaAccess, adminPageAccess } from './roleHelpers.js'
import { normalizeLauncherTile } from './launcherTiles.js'

// Workspace apps formerly rendered by the AppsWidget (HOME_WORK +
// HOME_TEAM + HOME_INSIGHTS unions from pages/main.js). Single source
// of truth for the search dropdown. Row shape matches AppListItem
// state: { title, caption, icon, route }.
//
// i18n — `title` AND `caption` ARE POLYGLOT KEYS HERE, and an installed app's
// are NOT. These rows are shipped chrome, so they carry keys like any other
// chrome string (docs/I18N-CONVENTION.md). The SAME render sites also print
// the customer's own tiles — a module manifest's, an extension's, a stored
// `settings.apps` row's — whose titles are the customer's words. Both work
// through one call, `el.call('polyglot', …)`, with no branch: `translate()`
// returns the key itself on a miss, and a shipped key is a namespaced dotted
// path that is a member of the flat `shell.json` map while a customer's words
// are not. Membership of the bundle IS the distinction.
//
// The readers that must resolve, all of them (a new one is a raw key on
// screen — that is how the VIEWS tiles ended up printing `shell.viewNav.apps`
// after CanvasViewNav was keyed):
//   • components/SearchAppsMenu.js — SasHead (group), SatLabel (tile), the
//     tooltip + aria-label
//   • canvas/components/PageSlot.js — the apps rail's GroupLabel + row Label
//   • canvas/components/MinimizedDock.js — the dock chip (PageSlot's
//     `_slotEntryMeta` enriches a chip from these rows, and the chip is
//     PERSISTED, so it stores the key and translates on a later switch)
// The two places a title LEAVES the UI as durable text — the drag ref
// (chat/AI message bodies) and the navbar pin — stamp the RESOLVED words
// instead; see SearchAppTile's onDragstart. Never put a key into a wire
// format a human reads back.
//
// `category` is NOT a key: it is the internal join field SEARCH_APP_GROUPS
// filters on and is never rendered.
export const SEARCH_APPS = [
  // Work — day-to-day execution surfaces.
  {
    title: 'shell.viewNav.workspaceApps',
    caption: 'shell.launcher.workspaceApps.caption',
    icon: 'grid',
    iconBg: 'blue',
    category: 'Work',
    route: '/apps'
  },
  {
    title: 'shell.viewNav.websites',
    caption: 'shell.launcher.studio.caption',
    icon: 'layers',
    glyph: 'studio',
    iconBg: 'blue',
    category: 'Work',
    route: '/studio'
  },
  // Infrastructure — the /infra canvas (services, environments, status).
  // Nika 2026-09-09 "make Infrastructure as one builtin app as others": the
  // route, its pages and its VIEW lens existed, but no launcher tile, navbar
  // row or home tile listed it. Icon + hue mirror the infra lens
  // (CanvasViewNav VIEW_NAV 'server', VIEW_TILE_BG cerulean).
  {
    title: 'shell.launcher.infra.title',
    caption: 'shell.launcher.infra.caption',
    icon: 'server',
    glyph: 'infra',
    iconBg: 'cerulean',
    category: 'Work',
    route: '/infra',
    // A developer tool: off a business workspace's launcher (persona audit
    // WH-8 — see searchAppGroupsFor).
    dev: true
  },
  // Marketplace moved OUT of the tile grid into the launcher header as the
  // "App Store" action (2026-07-30 per Nika "remove marketplace from search
  // dropdown … rename marketplace with app store").
  {
    title: 'shell.launcher.tasks.title',
    caption: 'shell.launcher.tasks.caption',
    icon: 'check',
    glyph: 'tasks',
    iconBg: 'green',
    category: 'Work',
    route: '/tasks'
  },
  // Documents + Files were missing from the launcher entirely (2026-08-05 per
  // Nika "why documents and files are not in search dropdown") even though both
  // own a navbar slot and a Home tile. Title / icon / route mirror
  // shared/components/AppNavbar.js's NAV_APPS + DROPDOWN_APPS entries exactly,
  // and they sit here in that same tickets → documents → files order.
  {
    title: 'shell.launcher.notes.title',
    caption: 'shell.launcher.notes.caption',
    icon: 'document',
    glyph: 'notes',
    iconBg: 'cerulean',
    category: 'Work',
    route: '/notes'
  },
  {
    title: 'shell.launcher.files.title',
    caption: 'shell.launcher.files.caption',
    icon: 'folder',
    glyph: 'files',
    iconBg: 'orange',
    category: 'Work',
    route: '/files'
  },
  {
    title: 'shell.launcher.chat.title',
    caption: 'shell.launcher.chat.caption',
    icon: 'chat',
    glyph: 'chat',
    iconBg: 'cerulean',
    category: 'Work',
    route: '/chat'
  },
  // Mail — the built-in email client (architecture/MAIL.md). The route, its
  // PageSlot registration (APP_WINDOW_ROUTES below), the header envelope and
  // both navbar copies all existed; the launcher tile was the one listing
  // that was still missing. Glyph mirrors the header button and the two
  // AppNavbar rows exactly ('email'), and it sits directly after Chat here
  // because that is the order both navbar copies already use (chat → mail →
  // meet). No hand-carried flag: `searchAppGroupsFor` filters every tile
  // through `navItemVisible`, whose ROUTE_FLAGS maps '/mail' → the
  // dark-by-default `mail` feature flag — the same one resolver the header
  // button and the navbar rows gate on, so the tile appears exactly when
  // they do and never on a workspace that has Mail switched off.
  {
    title: 'shell.launcher.mail.title',
    caption: 'shell.launcher.mail.caption',
    icon: 'email',
    glyph: 'mail',
    iconBg: 'blue',
    category: 'Work',
    route: '/mail'
  },
  {
    title: 'shell.launcher.calendar.title',
    caption: 'shell.launcher.calendar.caption',
    icon: 'date outline',
    glyph: 'calendar',
    iconBg: 'red',
    category: 'Work',
    route: '/calendar'
  },
  {
    title: 'shell.launcher.meet.title',
    caption: 'shell.launcher.meet.caption',
    icon: 'video',
    glyph: 'meet',
    iconBg: 'blue',
    category: 'Work',
    route: '/meet'
  },
  {
    title: 'shell.launcher.reminders.title',
    caption: 'shell.launcher.reminders.caption',
    icon: 'clockOutline',
    glyph: 'reminders',
    iconBg: 'red',
    category: 'Work',
    route: '/reminders'
  },
  // History — the built-in timeline of the workspace activity log. Gated
  // like every tile here: navItemVisible resolves '/history' through
  // ROUTE_FLAGS to the `history` flag.
  {
    title: 'shell.launcher.history.title',
    caption: 'shell.launcher.history.caption',
    icon: 'sfHistory',
    glyph: 'history',
    iconBg: 'purple',
    category: 'Work',
    route: '/history'
  },
  {
    title: 'shell.launcher.broadcast.title',
    caption: 'shell.launcher.broadcast.caption',
    icon: 'bell',
    glyph: 'broadcast',
    iconBg: 'purple',
    category: 'Work',
    route: '/announcements'
  },
  // Team — people + agents.
  {
    title: 'shell.launcher.teamAndAgents.title',
    caption: 'shell.launcher.teamAndAgents.caption',
    icon: 'people',
    glyph: 'teamAndAgents',
    iconBg: 'purple',
    category: 'Team',
    route: '/team-and-agents'
  },
  {
    title: 'shell.launcher.agents.title',
    caption: 'shell.launcher.agents.caption',
    icon: 'star',
    glyph: 'agents',
    iconBg: 'orange',
    category: 'Team',
    route: '/agents'
  },
  {
    title: 'shell.launcher.teamMembers.title',
    caption: 'shell.launcher.teamMembers.caption',
    icon: 'user',
    glyph: 'teamMembers',
    iconBg: 'cerulean',
    category: 'Team',
    route: '/team-members'
  },
  {
    title: 'shell.launcher.users.title',
    caption: 'shell.launcher.users.caption',
    icon: 'globe',
    glyph: 'users',
    iconBg: 'blue',
    category: 'Team',
    route: '/team-members'
  },
  // Insights — observability surfaces.
  {
    title: 'shell.launcher.analytics.title',
    caption: 'shell.launcher.analytics.caption',
    icon: 'analytics',
    glyph: 'analytics',
    iconBg: 'green',
    category: 'Insights',
    route: '/analytics'
  },
  // Workspace usage dashboard — the "Workspace" tab of the merged analytics
  // surface (AnalyticsNavbar.js), previously reachable only by opening
  // Analytics first and switching tabs. Same 'analytics' feature-flag scope
  // as the Analytics tile above (featureFlags.js / appAccess.js both map
  // '/workspace-analytics' to the 'analytics' flag), so it appears/hides in
  // lockstep with it. 2026-08-09 audit: /workspace-analytics rendered but had
  // no launcher entry point of its own.
  {
    title: 'shell.launcher.workspaceUsage.title',
    caption: 'shell.launcher.workspaceUsage.caption',
    icon: 'analytics',
    glyph: 'usage',
    iconBg: 'purple',
    category: 'Insights',
    route: '/workspace-analytics'
  }
]

// THE ROUTE'S LAUNCHER KEY — the title KEY of the built-in launcher tile that
// opens `route` exactly (no query, no hash), or ''. HeaderTabs hands it to
// applyNavbarTabPrefs so a custom pin saved as plain text before pins stored
// keys (e.g. { path: '/history', label: 'Workspace History' }) renders in the
// active language (shared __tests__/pinnedTabLabelKey.test.js).
export function launcherTitleKeyFor(route) {
  const p = String(route || '')
  if (!p || p !== p.split('#')[0].split('?')[0]) return ''
  const tile = SEARCH_APPS.find((t) => t && t.route === p)
  return tile && typeof tile.title === 'string' ? tile.title : ''
}

// THE LAUNCHER GLYPH (dropdowns spec 2026-09-30 §3.3; Nika's pick: the launcher wears the PIXEL glyphs) — the
// one shared resolver (shared/functions/glyphNames.js glyphNameFor) in the pixel set: a tile's own `glyph` (a
// built-in row, or a module app's manifest), then the route, then the app's own brand icon.
export function launcherGlyphName(tile) {
  return _wsShared.functions.glyphNameFor.call(this, tile, 'px')
}

// VIEWS section — the four canvas lenses, mapped from CanvasViewNav's VIEW_NAV
// into the springboard tile shape ({ title, icon, iconBg, route }). Routes stay
// single-sourced in VIEW_NAV; only the per-tile gradient colour is assigned
// here (VIEW_NAV carries no iconBg). Tiles navigate via selectAppFromSwitcher →
// openAppPage, same as every other app.
const VIEW_TILE_BG = {
  workspaceApps: 'blue',
  websites: 'green',
  frontend: 'blue',
  infra: 'cerulean',
  tasks: 'green',
  agents: 'purple'
}
// The canvas lenses are the developer's lab (the Frontend / Infra / Agents
// canvases): every view tile is a developer tool (persona audit WH-8). A
// business workspace keeps Apps and Websites through the Work group.
const _searchViewApps = () =>
  getViewNav().map((v) => ({
    title: v.label,
    icon: v.icon,
    iconBg: VIEW_TILE_BG[v.key] || 'blue',
    category: 'Views',
    route: v.path,
    dev: true
  }))

// Ordered category groups for the springboard sections. VIEWS follows the house
// app categories so the canvas lenses read as their own group at the foot of the
// launcher.
//
// A FUNCTION, not a module-scope constant — it closes over getViewNav()
// above, which must stay deferred to call time (see that const's own
// comment). Every consumer already calls this lazily (searchAppGroupsFor
// runs inside a `children: (el, s) =>` render factory).
export const SEARCH_APP_GROUPS = () => [
  // `category` stays an ENGLISH JOIN KEY — it is the internal field every
  // SEARCH_APPS row is filtered by, never rendered. `label` is what a reader
  // sees, so it is the polyglot key. Keeping the two apart is why translating
  // the launcher did not have to touch a single row's `category`.
  ...[
    ['Work', 'shell.launcher.groupWork'],
    ['Team', 'shell.launcher.groupTeam'],
    ['Insights', 'shell.launcher.groupInsights']
  ].map(([category, label]) => ({
    key: category.toLowerCase(),
    label,
    apps: SEARCH_APPS.filter((a) => a.category === category)
  })),
  { key: 'views', label: 'shell.launcher.groupViews', apps: _searchViewApps() }
]

// Admin surfaces — appended as their own launcher group ONLY for a person
// whose role opens an /admin page, each tile only while its page does not
// send them away, so the entry points appear exactly for whoever can open
// them (2026-07-25 per Nika "add admin pages in search dropdown menu thats
// only visible to admins"; the per-page gate since 3c — see
// `searchAppGroupsFor` below).
// Exported for the glyph route census (__tests__/glyphRouteCensus.test.js): every tile here resolves to a LINE glyph.
export const ADMIN_APPS = [
  {
    title: 'shell.launcher.adminConsole.title',
    caption: 'shell.launcher.adminConsole.caption',
    icon: 'settings',
    iconBg: 'red',
    category: 'Admin',
    route: '/admin',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'tools'
  },
  {
    title: 'shell.launcher.moduleConsole.title',
    caption: 'shell.launcher.moduleConsole.caption',
    icon: 'blocks',
    iconBg: 'purple',
    category: 'Admin',
    route: '/admin/module',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'apps',
    // a developer tool (persona audit WH-8)
    dev: true
  },
  {
    title: 'shell.launcher.permissions.title',
    caption: 'shell.launcher.permissions.caption',
    icon: 'lock',
    iconBg: 'orange',
    category: 'Admin',
    route: '/admin/permissions',
    // the line glyph (glyphNames.js reads only the first route segment)
    glyph: 'lock'
  },
  {
    title: 'shell.launcher.teams.title',
    caption: 'shell.launcher.teams.caption',
    icon: 'people',
    iconBg: 'cerulean',
    category: 'Admin',
    route: '/admin/teams',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'teamMembers'
  },
  {
    title: 'shell.launcher.usage.title',
    caption: 'shell.launcher.usage.caption',
    icon: 'analytics',
    iconBg: 'green',
    category: 'Admin',
    route: '/admin/usage',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'usage'
  },
  {
    title: 'shell.launcher.adminLogs.title',
    caption: 'shell.launcher.adminLogs.caption',
    icon: 'document',
    iconBg: 'blue',
    category: 'Admin',
    route: '/admin/logs',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'history'
  },
  {
    title: 'shell.launcher.featureFlags.title',
    caption: 'shell.launcher.featureFlags.caption',
    icon: 'state',
    iconBg: 'green',
    category: 'Admin',
    route: '/admin/flags',
    // the line glyph (glyphNames.js reads only the first route segment)
    glyph: 'flag',
    // a developer tool (persona audit WH-8)
    dev: true
  },
  {
    title: 'shell.launcher.systemHealth.title',
    caption: 'shell.launcher.systemHealth.caption',
    icon: 'state',
    iconBg: 'phosphorus',
    category: 'Admin',
    route: '/admin/system',
    // the line glyph (glyphNames.js reads only the first route segment, '/admin')
    glyph: 'health',
    // a developer tool (persona audit WH-8)
    dev: true
  },
  {
    title: 'shell.launcher.data.title',
    caption: 'shell.launcher.data.caption',
    icon: 'database',
    iconBg: 'purple',
    category: 'Admin',
    route: '/data/projects',
    // the line glyph (glyphNames.js reads only the first route segment)
    glyph: 'database',
    // a developer tool (persona audit WH-8)
    dev: true
  }
]

// Build the ordered section list for the apps menu. When the active workspace
// has a PROJECT with an apps section (unpacked into root.workspaceProject.config
// by loadWorkspaceProject, or hydrated into root.workspaceSettings.apps from a
// Mongo settings row), prepend it — labeled by the workspace name — at the TOP
// of the menu; otherwise return the house groups unchanged. Generic + workspace
// -scoped: no workspace is special-cased, and the section never leaks into a
// workspace whose project/settings don't define it.
export const searchAppGroupsFor = (root) => {
  const rawApps = root?.workspaceProject?.config?.apps || root?.workspaceSettings?.apps
  // Normalize + dedupe the workspace group. Both sources can carry thin rows
  // (an extension's { label, path } ref, a stored bare { route } row, a
  // manifest tile without icon/iconBg) — normalizeLauncherTile is the ONE
  // canonical guard that maps them to the full { title, icon, iconBg, route }
  // contract so a tile can never render nameless with an empty glyph (the
  // natali blank-tiles bug, 2026-07-30). Dedupe by route||title —
  // resolveWorkspaceConfig appends installed apps' tiles onto config.apps, and
  // a module that also lists its own tiles produced doubles (CMS/Contacts ×2,
  // live 2026-07-25).
  const seenApp = new Set()
  const apps = (Array.isArray(rawApps) ? rawApps : []).map(normalizeLauncherTile).filter((a) => {
    if (!a) return false
    const k = String(a.route || a.title || '').toLowerCase()
    if (!k || seenApp.has(k)) return false
    seenApp.add(k)
    return true
  })
  // Root cause of the persistent duplicate-Admin-section bug (Fable
  // 2026-08-09): when `apps.length` was falsy this used to assign the
  // MODULE-LEVEL CONSTANT `SEARCH_APP_GROUPS` directly (no copy) to `groups`,
  // and the `.push()` below then mutated that shared singleton IN PLACE —
  // every dropdown open in a workspace with no launcher tiles (any org whose
  // config/settings carry no `apps`, e.g. a fresh install or right after an
  // uninstall drops the last app) permanently appended ANOTHER 'Admin' group
  // onto the constant every session touched it. Repeat installs/uninstalls
  // (and the system-org HQ workspace, which has no custom launcher tiles)
  // compounded it fast. Always build a NEW array here — `.push()` must never
  // reach a module-level constant.
  const groups = apps.length
    ? [
        {
          // i18n — the two leading reads are the CUSTOMER'S OWN workspace
          // name and must reach the renderer untouched; only the last-resort
          // fallback is shipped chrome, so only it is a polyglot key. The
          // render sites (SearchAppsMenu SasHead, PageSlot's rail GroupLabel)
          // pass every label through `polyglot`, which resolves the key and
          // returns a name it has never heard of unchanged — so one call
          // serves both without a branch here.
          key: 'workspace-apps',
          label:
            (root?.workspace || {}).displayName ||
            (root?.workspace || {}).name ||
            'shell.app.workspace',
          apps
        },
        ...SEARCH_APP_GROUPS()
      ]
    : [...SEARCH_APP_GROUPS()]
  // Admin group — appended last. Explicit `key` (not just
  // `label`) so SamSections' childrenAs:'state' reconciles this and every
  // other group by stable IDENTITY rather than array position — the same
  // class of bug the navbar arrangement comment above warns about, just
  // hitting from the opposite direction (a shifting group COUNT, not a
  // shared key), and a stable key forecloses it regardless of mechanism.
  //
  // IT FOLLOWS EACH PAGE'S OWN GATE (3c; concierge decision 2026-10-31 23:35,
  // the admin menus follow the page's own gate). The group shows when the
  // person's role opens at least one /admin page (roleHelpers
  // `adminAreaAccess`: the rule of the account menu's Admin entry,
  // functions/adminRail.js `canOpenAdmin`, asked with no element, because
  // this builder runs bare: SearchAppsMenu, the ⌘K search). A tile hides when
  // its page answers 'closed' (roleHelpers `adminPageAccess`, the gate that
  // page runs); a page still waiting for its inputs keeps its tile, as the
  // page keeps the person. The Admin console tile opens the admin area
  // itself: /admin forwards a person Users & Roles keeps out to the first
  // page their role opens (pages/admin/admin.js), so it shows whenever the
  // group does. A route the gate table does not know (/data/projects: the
  // Data app has no role gate of its own) keeps its tile. Before: the whole
  // group, every tile, on `currentUserIsAdmin`, so a workspace admin who is
  // only an org member had no Admin group, and a person some admin pages
  // send away saw their tiles.
  if (adminAreaAccess(root) === 'open' && !groups.some((g) => g && g.key === 'admin')) {
    const adminApps = ADMIN_APPS.filter(
      (a) => a.route === '/admin' || adminPageAccess(root, a.route) !== 'closed'
    )
    groups.push({ key: 'admin', label: 'shell.launcher.groupAdmin', apps: adminApps })
  }
  // Feature-flag gate: tiles whose route maps to a disabled flag drop out
  // (navItemVisible / ROUTE_FLAGS); groups that empty out disappear whole.
  //
  // Per-VIEWER gate (GA D1 "railless" follow-through): a module config tile
  // may carry `visible(root, tile) → boolean` — a frank-revived function the
  // MODULE ships (workspace config functions arrive live through the module
  // loader), so persona/role trimming stays module-owned while the shell
  // stays abstract. Absent → shown; a throwing gate also shows (a broken
  // module fn must never hide navigation). Server settings rows are plain
  // JSON and can't carry functions — this is deliberately a module-config
  // capability only.
  const viewerVisible = (a) => {
    if (typeof a?.visible !== 'function') return true
    try {
      return a.visible(root, a) !== false
    } catch (err) {
      console.warn('[searchApps] tile visible() threw — showing tile:', a?.title, err?.message)
      return true
    }
  }
  // A BUSINESS workspace keeps the developer tools out of its launcher
  // (persona audit WH-8: a clinic's launcher carried Infrastructure, Module
  // console, Feature flags, System health and Data). The workspace attribute
  // is `settings.preset` — the work its owner chose (an industry or
  // "Something else"; CONTRACT §1, functions/adminRail.js
  // workspaceIsBusiness); the tile's own `dev: true` says which tools are a
  // developer's. A workspace with no preset keeps every tool. The tools stay
  // reachable from the admin rail's "Developer tools" group.
  const _preset = root?.workspace?.settings?.preset
  const _business = !!(_preset && typeof _preset.key === 'string' && _preset.key)
  const seenRoutes = new Set()
  return groups
    .map((g) => ({
      ...g,
      apps: (g.apps || []).filter((a) => {
        if (_business && a && a.dev === true) return false
        if (!shellNavItemVisible(root, a) || !viewerVisible(a)) return false
        const route = shellStripWorkspacePrefix(String(a.route || '')).replace(/\/$/, '') || '/'
        if (seenRoutes.has(route)) return false
        seenRoutes.add(route)
        return true
      })
    }))
    .filter((g) => g.apps.length)
}

// THE LAUNCHER'S TWO SEARCH MODELS — one per entry point (Nika 2026-10-28:
// "lets keep it for apps filtering, and main search will also stay as is").
// openSearchAppsDropdown writes the open panel's mode to `root._launcherMode`:
//   • 'trigger' — a navbar chevron ("Open apps") or the home App strip's "All
//     apps" opened the panel. It owns its filter field (SearchAppsMenu
//     SamFilterField): typing writes `_launcherFilter`, narrows the tiles in
//     place and never writes `navSearchQuery` or runs the header search.
//   • 'companion' — the focused header search opened it (ensureSearchLauncher).
//     The filter row mirrors that field and typed text shows its results (9A).
// One launcher layer exists at a time (the `searchApps` dropdown), so one root
// key carries the mode; a panel mounted with no mode written (the UI-kit
// catalog) reads as the companion, which is what it was before.

// The launcher's tiles: the chosen category's groups (SearchAppCategory —
// every group on 'all' and on a group this workspace lacks), narrowed in the
// trigger mode by the panel's own filter. A tile matches on the name it prints
// (the store-copy resolver, in the current language) or on its caption, the
// one line that says what the app is. A group the filter empties is dropped,
// so its head never draws over nothing.
// el.call('launcherTileGroups', root)
export function launcherTileGroups(root) {
  const groups = searchAppGroupsFor(root)
  const selected = root?._launcherGroup || 'all'
  const shown =
    selected === 'all' || !groups.some((g) => g.key === selected)
      ? groups
      : groups.filter((g) => g.key === selected)
  const query =
    root?._launcherMode === 'trigger'
      ? String(root._launcherFilter || '')
          .trim()
          .toLowerCase()
      : ''
  if (!query) return shown
  const matches = (app) => {
    const name = this.call('storeItemText', { key: app.appKey, name: app.title }, 'name')
    const caption = app.caption ? this.call('polyglot', app.caption) : ''
    return `${name || ''} ${caption || ''}`.toLowerCase().includes(query)
  }
  return shown
    .map((g) => ({ ...g, apps: (g.apps || []).filter(matches) }))
    .filter((g) => g.apps.length)
}

// Routes the home AppWindow (PageSlot) can mount — MUST stay in sync
// with `_APP_WINDOW_PAGES` in pages/main.js (the map pairs these keys
// with imported page objects; this set is the call-site-safe mirror
// for code that can't import the page module without a cycle).
export const APP_WINDOW_ROUTES = new Set([
  // Every NON-CANVAS app in the search dropdown opens inside the shell
  // PageSlot — paired with `_APP_WINDOW_PAGES` in pages/main.js for page
  // registration (2026-06-08 per Nika). The canvas surfaces that also appear
  // below (/studio, /tickets, /team-and-agents, /agents, /my-agents) are in
  // the set ONLY for that registration pairing: `openAppPage`'s `isCanvasRoute`
  // gate (checked first) routes them as a FULL canvas page, never the slot —
  // they carry fixed/absolute children + their own scratch config and flicker
  // when mounted in the slot. Rule: non-canvas apps → PageSlot, canvas pages →
  // full page (per Nika 2026-07-16).
  '/studio',
  // Marketplace is a non-canvas app — opens in the PageSlot. PageSlot's _defFor
  // resolves it via _CANVAS_SLOT_PAGES (canvas/components/PageSlot.js), so it
  // mounts in-slot without an _APP_WINDOW_PAGES entry (that pairing only adds
  // the `?page=/marketplace` reload-restore on the home route).
  '/marketplace',
  // `/add-app` sits in the SAME launcher header as the App Store and Following
  // buttons (SearchAppsMenu.js), all three opening via `openAppPage`. With the
  // other two now landing in the slot, leaving this one full-page would make
  // three adjacent buttons behave differently for no reason the user can see
  // (2026-08-09 per Nika "mostly all non-canvas pages should open in
  // pageslot"). Non-canvas, so no _APP_WINDOW_PAGES pairing is needed to
  // mount — that pairing only adds the `?page=` reload-restore.
  '/add-app',
  '/chat',
  // Mail — the built-in email client (architecture/MAIL.md), a non-canvas
  // app: opens in the PageSlot, paired with `_APP_WINDOW_PAGES` in
  // pages/main.js. Its SEARCH_APPS launcher tile is registered above
  // (strings: `shell.launcher.mail.*` in shell.json).
  '/mail',
  // Canonical /tasks + legacy /tickets aliases (tickets→tasks rename
  // 2026-08-06) — both forms stay registered for the `_APP_WINDOW_PAGES`
  // pairing; isCanvasRoute still routes the board full-page.
  '/tasks',
  '/tasks/my',
  '/tickets',
  '/tickets/my',
  '/calendar',
  '/reminders',
  // History — a non-canvas app: opens in the PageSlot, paired with
  // `_APP_WINDOW_PAGES` in pages/main.js.
  '/history',
  '/notes',
  '/documents',
  // Merged analytics: /analytics = Projects family (overview + map +
  // logs), /workspace-analytics = Workspace usage. All open in the
  // PageSlot — paired with `_APP_WINDOW_PAGES` in pages/main.js.
  '/analytics',
  '/analytics/map',
  '/analytics/sessions',
  // Projects-family views registered in _APP_WINDOW_PAGES (pages/main.js)
  // but missing here: visitors (WS-ANALYTICS-UTM-CAMPAIGNS-UI-1) + bugs /
  // events (pre-existing gap).
  '/analytics/visitors',
  '/analytics/bugs',
  '/analytics/events',
  '/analytics/logs/users',
  '/analytics/logs/sessions',
  '/analytics/logs/session',
  '/workspace-analytics/logs/session',
  '/analytics/logs/events',
  '/analytics/logs/bugs',
  '/analytics/logs/network',
  '/analytics/logs/verbose',
  '/workspace-analytics',
  '/workspace-analytics/map',
  '/workspace-analytics/logs/users',
  '/team-and-agents',
  '/agents',
  '/my-agents',
  // '/studio' + the canvas surfaces above are kept in the set for the
  // `_APP_WINDOW_PAGES` registration pairing (2026-06-10 per Nika "all apps in
  // SearchDropdown should be reachable"). They still open FULL-PAGE via
  // isCanvasRoute — the flicker that first excluded /studio is exactly why the
  // canvas gate keeps them out of the slot (see the header comment above).
  '/meet',
  '/announcements',
  '/team-members',
  '/system-status',
  // '/admin' kept ONLY for the `_APP_WINDOW_PAGES` ?page= registration
  // pairing — it no longer opens in the slot: isFullPageRoute short-circuits
  // openAppPage to full-page above (2026-07-25 per Nika "admin pages should
  // not open in pageSlot"). Admin is a full settings surface with its own
  // AdminSidebar.
  '/admin'
  // (Workspace-MODULE routes are NOT listed here — they resolve dynamically via
  // root.workspaceProject.pages: openAppPage's isProjectPage branch below and
  // pages/main.js `_appPageFor` consult the active module's page map, so module
  // routes exist ONLY in the workspace whose module registered them.)
])

// Full-bleed pan/zoom CANVAS surfaces — these MUST land as the full canvas
// (a hard top-level route) and never inside the windowed home PageSlot, even
// from home. Mirrors the 'canvas' route-kinds in pages/app.js (the surfaces
// that get <html data-shell-route="canvas">): /studio, /canvas, /files,
// /infra, /tickets (board route only), /team-and-agents, /agents, /my-agents.
// Without this, a header-navbar tab to Studio/Tasks/Agents/Infra from `/`
// opened cramped in the AppWindow PageSlot instead of landing the canvas —
// the "sometimes the navbar opens a PageSlot when it should land the canvas"
// bug. These are real top-level routes, so router() never 404s them.
const _CANVAS_ROUTE_RE = /^\/(studio|canvas|files|infra|team-and-agents|agents|my-agents)(\/|$)/
export function isCanvasRoute(route) {
  const p = shellStripWorkspacePrefix(
    String(route || '')
      .split('?')[0]
      .split('#')[0]
  )
  // /tasks (canonical) and /tickets (legacy alias) are canvas ONLY for the
  // board route; /tasks/my, /tasks/* are windowed app pages (matches
  // _isTicketsCanvasPath in pages/app.js).
  return p === '/tasks' || p === '/tickets' || _CANVAS_ROUTE_RE.test(p)
}

// D2 §8.2 — the CANVAS surfaces a DELIBERATE split may put in a pane.
//
// A ticket opens beside what you are reading and a file opens as
// `/files?node=<key>`; both are canvas routes, so `isCanvasRoute` above sent
// them full-page before `openAppPage` ever reached its split branch, and a
// split of either was impossible. Measured 2026-09-10 on the local shell
// against staging: `openAppPage('/tasks?ticket=…', { preferSlot: true,
// split: true })` from inside an open pane replaced the whole shell with the
// tasks board and never wrote `_canvasSlotSecond`.
//
// Deliberately NARROWER than `isCanvasRoute`: /studio, /canvas, /infra,
// /agents, /team-and-agents and /my-agents carry fixed/absolute children and
// their own scratch config, and they still have no pane form. Only the two
// surfaces D2 names are listed. The allowance is gated on INTENT at the call
// site (see `openAppPage`), never on the route alone — a plain navbar or
// launcher click still lands the canvas.
const _PANEABLE_CANVAS_RE = /^\/(tasks|tickets|files)(\/|$)/
export function isPaneableCanvasRoute(route) {
  return _PANEABLE_CANVAS_RE.test(
    shellStripWorkspacePrefix(
      String(route || '')
        .split('?')[0]
        .split('#')[0]
    )
  )
}

// D2 §8.4 — below this CSS width there is no split. Two panes are ~460px
// each on a 1000px window, and a room list, a reading pane or a board is
// unreadable in one; the module's own menu already DISABLES "Open in split
// pane" under it (vaultCanSplit). The shell needs the same floor because a
// split can also arrive from the header's split button, a cmd/ctrl-click or
// a resize after the fact — and `openAppPage` split at ANY width, so a phone
// got two ~190px panes.
export const SPLIT_MIN_WIDTH_PX = 1024

// Pure over the width so the branch is testable without a window. A width of
// 0 (SSR, a test harness, a headless probe with no layout) answers TRUE: a
// capability must not report itself missing because nothing measured a
// screen — same rule the module's `vaultCanSplit` states.
export function splitFitsWidth(width) {
  const w = Number(width) || 0
  return !w || w >= SPLIT_MIN_WIDTH_PX
}

const _viewportWidth = () =>
  typeof window !== 'undefined' && window ? Number(window.innerWidth) || 0 : 0

// The state a FOLDED split leaves behind — the same four keys PageSlot's own
// second-pane dismissal writes (`_dismissPane(root, false)`, canvas/components/
// PageSlot.js): the pane's route, any in-progress split intent, and the pane's
// own history + rail flags, which are a lineage that dies with it. Declared
// once here so the two shell callers below cannot drift from each other.
const _FOLD_SPLIT = {
  _canvasSlotSecond: null,
  _slotSplitIntent: false,
  _slotHistSecond: null,
  _slotRailSecond: false,
  // one pane left: a "Keep this pane open" pin has nothing to keep apart
  _slotPinned: null
}

// D2 §8.4, the half the floor was missing. `splitFitsWidth` only ever gated
// the OPENING of a second pane, so a split opened on a wide window SURVIVED
// every way of getting narrow:
//
//   • resize 1440 → 1000 and the two panes just get smaller. MEASURED on the
//     deployed staging shell (64bab2690, vault.pr, 2026-09-10): at a verified
//     `window.innerWidth` of 1000 the shell showed PanePrimary 491px +
//     PaneSecond 491px, while the module's own `vaultCanSplit()` answered
//     false beside it — the shell disagreeing with the module about whether a
//     split exists on the very screen showing one.
//   • and every later open kept it: the width guard refused the NEW split and
//     fell through to a plain primary write that never touched the standing
//     second pane, so the same measurement after `openAppPage('/notes',
//     { preferSlot: true, split: true })` was still two 491px panes.
//
// The rule this restores is the simple one the floor always meant: BELOW THE
// FLOOR THERE IS EXACTLY ONE PANE. The PRIMARY is the pane that survives —
// same choice the narrow open already made ("a split request opens the route
// in the PRIMARY pane instead"), and the pane the user's context lives in.
//
// Returns true when it actually folded something, so a resize storm writes
// state once and every later tick is a no-op.
export function foldSplitBelowWidthFloor(root, width) {
  if (!root || typeof root.update !== 'function') return false
  if (splitFitsWidth(width)) return false
  if (!root._canvasSlotSecond && !root._slotSplitIntent) return false
  root.update({ ..._FOLD_SPLIT })
  return true
}

// The registered shell function behind the shell root's `onWindowResize`
// (app.js) — el.call('foldSplitOnResize'). Reads the LIVE viewport rather
// than the event, because a resize event carries no width and the guard must
// read the same `window.innerWidth` `openAppPage` reads.
export function foldSplitOnResize() {
  const root = this?.state?.root || this?.state
  return foldSplitBelowWidthFloor(root, _viewportWidth())
}

// STRICT pane probe — "is this caller INSIDE a PageSlot pane?".
//
// `slotPaneKeyFor` (shared) answers the WRITER's question, "which pane state
// key should this opener write", and DELIBERATELY falls through to the
// primary for a caller that is in no pane at all (navbar, launcher, toast).
// That default is right for the write and useless for this decision: a navbar
// click and an in-pane click both read '_canvasSlot' from it. Returns the
// stamp or null. Hop-capped exactly like its sibling. It lives here because
// packages/shared is read-only for this squad.
const _callerPaneKey = (el) => {
  let node = el
  let hops = 0
  while (node && hops < 100) {
    const key = node._slotPaneKey
    if (key === '_canvasSlot' || key === '_canvasSlotSecond') return key
    if (node.parent === node) break
    node = node.parent
    hops++
  }
  return null
}

// Full-page SETTINGS destinations — /admin/* and /data/* carry their OWN
// AdminSidebar nav, so they must land as a real top-level page, never the
// cramped modal PageSlot: a sidebar-in-a-sheet can't drive slot routing,
// so its links fired shell navigations that left the slot open over a
// stale page (2026-07-25 per Nika "fix admin pages opening in pageSlot
// from search dropdown"). Like isCanvasRoute, this wins over the
// APP_WINDOW_ROUTES / preferSlot branches below.
// Unlike ordinary app navigation (which can explicitly split), these surfaces
// always own the page, including explicit pane requests.
const _FULLPAGE_ROUTE_RE = /^\/(admin|data)(\/|$)/
const _routePath = (route) =>
  shellStripWorkspacePrefix(String(route || '').split(/[?#]/)[0]).replace(/\/+$/, '') || '/'

export function isFullPageRoute(route) {
  return _FULLPAGE_ROUTE_RE.test(_routePath(route))
}

// Navigation reads paths, not rendered tabs: loading and presentation flags
// must not discard already-known configuration or resolve translated labels.
const _navbarOwnsAppRoute = (route, root) => {
  const { components, functions } = _wsShared
  const prefs = functions.navbarTabPrefs(root)
  const { settings, ready, workspace } = functions.workspaceSettings(root)
  const lc = (value) => String(value || '').toLowerCase()
  const org = lc(root?.org?.id || root?.org?._id)
  const ownsRecord =
    ready && org && org === lc(workspace.organization || workspace.organizationId || workspace.org)
  const saved = root?.workspaceSettings?.navbar
  const config =
    Array.isArray(saved) && saved.length
      ? saved
      : ownsRecord && Array.isArray(settings?.navbar)
        ? settings.navbar
        : []
  const path = _routePath(route)
  const canonical = functions.canonicalRoutePath
  const matches = (tab, custom = false) => {
    if (!tab?.path) return false
    const base = _routePath(tab.path)
    const suffix = String(tab.path).slice(String(tab.path).search(/[?#]|$/))
    // A record/view pin owns exactly that destination. Only an app-root pin
    // (one path segment) or a configured app tab owns descendant routes.
    if (suffix || (custom && base.split('/').filter(Boolean).length > 1)) {
      const targetSuffix = String(route).slice(String(route).search(/[?#]|$/))
      return canonical(base) === canonical(path) && suffix === targetSuffix
    }
    return components.HeaderTabs.scope.matchesPath(base, path)
  }
  return (
    components.HEADER_TABS.some((tab) => matches(tab)) ||
    config.some((tab) => !prefs.hidden.includes(tab?.path) && matches(tab)) ||
    prefs.pinnedCustom.some((tab) => !prefs.hidden.includes(tab?.path) && matches(tab, true))
  )
}
// Navbar apps open at their registered URLs; other apps use PageSlot.
// Explicit split/pin requests and in-pane links retain their current host.
// `{ pin: true }` — a module asks to KEEP the pane its route lands in (the
// VaultOS decision): after the open, the pane that shows the route is pinned
// (root._slotPinned). A route that left the slot (a full page) pins nothing.
// el.call('openAppPage', route, { pin: true })
export function openAppPage(route, opts) {
  const out = _openAppRoute.call(this, route, opts)
  if (opts && opts.pin) {
    const root = this?.state?.root || this?.state
    const at =
      root && route
        ? ['_canvasSlotSecond', '_canvasSlot'].find(
            (k) => root[k] && shellCleanHref(root[k]) === shellCleanHref(route)
          )
        : null
    if (at && root._slotPinned !== at) root.update({ _slotPinned: at })
  }
  return out
}

// Not `_openAppPage`: workspaceProjects.js declares its own `_openAppPage`
// (entry, path, el, s) for the opening placeholder, and frank lifts both into
// the shell's ONE flat globalScope under the bare name. frank 3.14.792 kept
// this one for both, so the PUBLISHED placeholder's onRender ran this route
// opener with an entry object; frank 3.14.800 refuses the pair
// (module-scope-name-conflict).
function _openAppRoute(route, opts) {
  if (!route) return
  const root = this?.state?.root || this?.state
  // D2 §5 (P1): a context menu is anchored to viewport coordinates on the
  // surface it was opened from, so every open dismisses it. The shell root's
  // `onRouteChanged` covers ROUTER navigations, but a PageSlot open writes
  // `_canvasSlot` and fires no route event at all — which is exactly the
  // navigation D2 measured a stray menu surviving TWICE (Data Rooms →
  // Decisions → board are all slot opens). One line at the single choke point
  // every opener already funnels through.
  try {
    this?.call?.('closeContextMenuOnRouteChange')
  } catch (_) {}
  // Gesture→intent mapping lives HERE, beside the mechanics it triggers:
  // callers may pass { split: true } explicitly OR forward their raw click
  // event — cmd/ctrl is normalized once for every opener (header tabs, search,
  // app tiles), so the split gesture can never drift per call site.
  // Split intent: an explicit gesture (cmd/ctrl-click, forwarded event) OR the
  // PageSlot header's split button, which arms `root._slotSplitIntent` so the
  // NEXT app pick lands in the second pane (consumed + cleared here).
  const split = !!((opts && (opts.split || opts.metaKey || opts.ctrlKey)) || root?._slotSplitIntent)
  if (root?._slotSplitIntent) root.update({ _slotSplitIntent: false })
  // Legacy callers still pass preferSlot. It keeps in-pane links in their
  // current host and lets installed app routes resolve in PageSlot.
  const preferSlot = !!(opts && opts.preferSlot)
  // PANE INTENT — the one thing that can put a D2 §8.2 canvas surface
  // (/tasks, /tickets, /files) in a pane instead of the full canvas. Two
  // shapes, both deliberate:
  //   • an explicit split gesture (`opts.split`, cmd/ctrl-click, or the
  //     header's split button via `_slotSplitIntent` — all already folded
  //     into `split` above), or
  //   • a `preferSlot` open fired from INSIDE an already-open pane (an
  //     in-app link, a module row's Open): the user is reading in a pane and
  //     asked for the next thing, so the next thing belongs in a pane too.
  // A navbar tab or a launcher tile is NEITHER — it carries no split gesture
  // and sits outside every pane — so it still lands the canvas. That is the
  // "sometimes the navbar opens a PageSlot when it should land the canvas"
  // bug (the reason `isCanvasRoute` exists at all), and it stays fixed.
  const callerPaneKey = _callerPaneKey(this)
  const fromOpenPane = !!(callerPaneKey && root && root[callerPaneKey])
  const paneIntent = split || (preferSlot && fromOpenPane)
  const appPath = shellStripWorkspacePrefix(String(route).split('?')[0].split('#')[0])
  // Chat has two entry surfaces but only one may be visible at a time:
  // navigating to its PageSlot dismisses the floating MessengerOverlay first.
  // Keep this at the route choke point so every HeaderTabs variant and every
  // other route opener observes the same singleton contract.
  if (appPath === '/chat' && root?._chatDrawerOpen) {
    root.update({ _chatDrawerOpen: false })
  }
  // A builder address that names an app (`/add-app?thread=<id>` — a deep
  // link, the search's Your apps rows, the builder's "Open <app>" line —
  // BLD-28) binds that app FIRST, so the builder opens on it wherever it
  // opens: the home's Build tab, a pane or the page (a pane reads no address
  // bar; /add-app keys its thread on `_addAppProjectId`).
  if (appPath === '/add-app' && root && typeof root.update === 'function') {
    const query = String(route).split('#')[0].split('?')[1] || ''
    const thread = new URLSearchParams(query).get('thread')
    if (thread && String(root._addAppProjectId || '') !== thread) {
      root.update({ _addAppProjectId: thread }, { preventFetch: true })
    }
  }
  // THE HOME'S BUILD TAB IS /add-app (functions/homeBuild.js; Nika
  // 2026-10-23: "build tab should reuse ui/ux of /add-apps"): on the home,
  // its tabs on screen, the builder opens as that tab — never a second
  // builder in a pane over it (both would hold the one thread and the one
  // render address). A deep link's thread binds the builder (above); a split
  // gesture still asks for a pane.
  const homeFns = (this && this.context && this.context.functions) || {}
  if (
    appPath === '/add-app' &&
    !paneIntent &&
    root &&
    typeof homeFns.homeTabsOnScreen === 'function' &&
    typeof homeFns.homeViewSelect === 'function' &&
    this.call('homeTabsOnScreen', root)
  ) {
    this.call('homeViewSelect', 'build')
    return true
  }
  // A plain app pick on its dedicated page keeps that existing surface and
  // its filters/draft. Explicit destinations and pane gestures still navigate.
  const currentPath = String(root?.currentPath || '')
    .split('?')[0]
    .split('#')[0]
  if (
    !paneIntent &&
    !callerPaneKey &&
    !root?._canvasSlot &&
    !root?._canvasSlotSecond &&
    !/[?#]/.test(route) &&
    currentPath &&
    shellStripWorkspacePrefix(currentPath) === appPath &&
    (!String(route).startsWith('/w/') || currentPath === route)
  ) {
    return true
  }
  // Navbar membership decides normal opens, including installed apps.
  // A deliberate split, an existing split/pin, or navigation within a pane
  // retains the pane host. Read membership on every open so pin/unpin and
  // workspace switches take effect without a reload or a route allowlist.
  const hasPaneSession = !!(
    fromOpenPane ||
    root?._canvasSlotSecond ||
    (root?._slotPinned && root[root._slotPinned])
  )
  if (!paneIntent && !hasPaneSession && !(opts && opts.pin) && _navbarOwnsAppRoute(route, root)) {
    if (root?._slotPinned) root.update({ _slotPinned: null })
    return this.router(route, this.getRoot())
  }
  const canvasMayPane = paneIntent && isPaneableCanvasRoute(route)
  // Canvas surfaces always land as the full canvas — never the windowed
  // PageSlot, even from home. (See isCanvasRoute above.) Admin + data
  // settings surfaces are full-page for the same "own sidebar nav" reason
  // (isFullPageRoute) and have NO pane form at all.
  // An installed module's canvas page (manifest `canvasRoutes`) lands as the
  // full canvas too — the root Zoombar drives it only on its own route.
  const moduleCanvas = !paneIntent && shellIsModuleCanvasRoute(_routePath(route), root)
  if ((isCanvasRoute(route) && !canvasMayPane) || moduleCanvas || isFullPageRoute(route)) {
    return this.router(route, this.getRoot())
  }
  // Slot lookups compare the PATH, never the raw href. Callers legitimately
  // pass query- and hash-carrying routes — the launcher's App Store button
  // sends `/marketplace?surface=appstore`, chat deep-links carry `?channel=`
  // — and an exact `Set.has()` on the raw string misses every one of them.
  // That is why the App Store opened as a FULL PAGE instead of the PageSlot
  // (2026-08-09 per Nika "opening appstore and mostly all non-canvas pages
  // should open in pageslot"): `/marketplace` is in the set, but
  // `/marketplace?surface=appstore` is a different string.
  //
  // `isCanvasRoute` and `isFullPageRoute` above already normalize exactly this
  // way; only this gate did not, so the three route tests disagreed with each
  // other about what a route even was. The FULL route (query + hash intact) is
  // still what gets opened — normalization decides, it never rewrites.
  const slotPath = _routePath(route)
  // A sub-path belongs to its app: `/marketplace/feed` is the marketplace,
  // `/analytics/logs/anything` is analytics. Registering every leaf explicitly
  // is what left `/marketplace/feed` (the launcher's Following button) opening
  // full-page while its own parent opened in the slot. Safe as a prefix test
  // because both "always full page" gates — isCanvasRoute and isFullPageRoute
  // — return above this point, so `/admin/*` and the canvas surfaces are
  // already gone even though `/admin` and `/tickets` sit in this set.
  const isAppWindowRoute =
    APP_WINDOW_ROUTES.has(slotPath) ||
    [...APP_WINDOW_ROUTES].some((r) => slotPath.startsWith(r + '/'))
  // A workspace-project page (root.workspaceProject.pages) opens in the PageSlot
  // too — these routes live only in the active workspace's project, so they're
  // not in the shared APP_WINDOW_ROUTES set.
  const isProjectPage = !!(
    root &&
    root.workspaceProject &&
    root.workspaceProject.pages &&
    (root.workspaceProject.pages[slotPath] || root.workspaceProject.pages[route])
  )
  // Non-canvas app + workspace-module pages open in the shell PageSlot from
  // ANY surface, not just home (2026-07-14 per Nika "all non-canvas pages
  // from search dropdown should open in PageSlot"). The old `path === '/'`
  // gate predates the slot move: PageSlot is now mounted once at the shell
  // root (app.js `PageSlot: {}`), so it exists on every page, and these
  // routes resolve everywhere via el.context.pages (shell registry + the
  // active module's injected pages). Canvas surfaces already returned above.
  if (isAppWindowRoute || isProjectPage || preferSlot || canvasMayPane) {
    // Split view (iPad style): `opts.split` (cmd/ctrl+click on a navbar tab)
    // opens the route in the SECOND PageSlot pane beside the current one.
    // Without an open primary there's nothing to split against — fall through
    // to a plain primary open. Same-route splits are a no-op re-focus.
    // NARROW WINDOW (D2 §8.4): below SPLIT_MIN_WIDTH_PX a split request opens
    // the route in the PRIMARY pane instead — the same open, one pane. The
    // shell owns this floor because a split can arrive without ever passing
    // the module's own disabled menu row: a cmd/ctrl-click, the pane header's
    // split button, or an opener that armed `_slotSplitIntent` on a wide
    // window and fired it after a resize. Without it `openAppPage` split at
    // ANY width and a phone got two ~190px panes. The module's own-item sheet
    // is untouched by this — it never reaches openAppPage.
    const fitsWidth = splitFitsWidth(_viewportWidth())
    // THE CALLER'S PANE (resolved once; rule (1) below reuses it). A split
    // ALWAYS opens in the pane OPPOSITE the caller (Nika 2026-10-09: "clicking
    // from a decision to other apps like data room opens in split view"): from
    // the left pane the right one, from the RIGHT pane the LEFT one — never the
    // caller's own pane, which used to happen when the caller sat in the second
    // pane (a decision there was replaced by the app it linked to). The target
    // pane is REPLACED, never stacked. A call from outside any pane keeps the
    // old target, the second pane. A split to the route the caller already
    // shows is no split (it falls through to the pane-scoped re-focus).
    const paneKey = this ? shellSlotPaneKeyFor(this) : '_canvasSlot'
    // ASK BEFORE REPLACING (Nika 2026-10-10): a split — and a call the pin
    // redirects — REPLACES a pane. When the page that pane SHOWS declares
    // unsaved work — the shell's one dirty rule, hasUnsavedInput (an `isDirty`
    // hook or a `dirty` flag) — ask with the one prompt, confirmDiscardChanges.
    // Keep (also Esc, the veil, a refused layer) leaves both panes as they
    // are; Discard replaces. The shown page is the host canvas PageSlot
    // records per pane (`_markLiveHost`); a parked keep-alive host is not
    // recorded, because a replace keeps it. An empty pane, a clean page, or a
    // surface without the shell prompt (canvas standalone) is replaced at once.
    const replaceAsking = (target) => {
      const replace = () => root.update({ [target]: route, _slotFullscreen: null })
      const live = root[target] && root.__slotLiveHosts && root.__slotLiveHosts[target]
      const fns = this?.context?.functions
      if (
        live &&
        live.__ref &&
        fns &&
        typeof fns.hasUnsavedInput === 'function' &&
        typeof fns.confirmDiscardChanges === 'function' &&
        this.call('hasUnsavedInput', live)
      ) {
        this.call('confirmDiscardChanges', { onDiscard: replace, onKeep: () => {} })
        return
      }
      replace()
    }
    // THE OPT-IN PIN (Nika 2026-10-10: "Yes, opt-in pin"). `root._slotPinned`
    // names the pane a user (the pane header's "Keep this pane open") or a
    // module (`{ pin: true }`) keeps. A pin on a pane that shows nothing is
    // stale, and below the split floor there is ONE pane, so the pin cannot
    // hold: both clear it. While it holds, a call from OUTSIDE every pane
    // (navbar, launcher, search — split or not) acts as a split from the
    // pinned pane: the route replaces the OTHER pane; a route the pinned pane
    // already shows is focus only. Nothing is pinned by default.
    let pinned =
      root._slotPinned === '_canvasSlot' || root._slotPinned === '_canvasSlotSecond'
        ? root._slotPinned
        : null
    if (root._slotPinned && (!pinned || !root[pinned] || !fitsWidth)) {
      root.update({ _slotPinned: null })
      pinned = null
    }
    if (pinned && !callerPaneKey) {
      if (shellCleanHref(root[pinned]) === shellCleanHref(route)) return
      replaceAsking(pinned === '_canvasSlot' ? '_canvasSlotSecond' : '_canvasSlot')
      return
    }
    if (split && fitsWidth && root._canvasSlot && root[paneKey] !== route) {
      // the pinned pane is never a split's target: the caller's own pane is
      let target = paneKey === '_canvasSlotSecond' ? '_canvasSlot' : '_canvasSlotSecond'
      if (target === pinned) target = paneKey
      replaceAsking(target)
      return
    }
    // BELOW THE FLOOR THERE IS EXACTLY ONE PANE — so the open lands in the
    // primary AND a split still standing from a wider window folds away, in
    // the SAME write. Without this half the guard only refused the NEW split:
    // it fell through to the pane-scoped branches below, which either wrote
    // the primary while the old second pane stayed on screen, or (for a
    // caller inside that second pane) wrote the second pane itself — both
    // measured live at a verified 1000px on the deployed staging shell as two
    // 491px panes. See `foldSplitBelowWidthFloor`.
    //
    // Unconditional on `split`: a PLAIN open is just as bound by the floor as
    // a split gesture is. The condition is the width, never the gesture.
    if (!fitsWidth && (root._canvasSlotSecond || root._slotSplitIntent)) {
      root.update({ _canvasSlot: route, ..._FOLD_SPLIT })
      return
    }
    // PANE-SCOPED targeting (split-view fix, 2026-08-06 per Nika "fix bug
    // when its on split view - it often changes both views on navigation
    // click"). Two rules, in order:
    //
    // (1) An open fired from INSIDE a pane lands in THAT pane. el.call binds
    //     `this` to the origin element, and PageSlot stamps `_slotPaneKey` on
    //     every pane + keep-alive host, so slotPaneKeyFor resolves the origin
    //     pane by a parent walk — an in-pane click (marketplace tile, in-app
    //     link) must never retarget the sibling pane.
    //
    // (2) A route already living in the SECOND pane re-focuses/deep-links
    //     THERE instead of also writing the primary (iPad semantics: tapping
    //     an app that's already in the split shows the split). This is ALSO
    //     the guard that restores this function's documented idempotency
    //     under double-fire openers: HeaderTabs fires _openTab on BOTH
    //     onMousedown (pointer path) and onClick (keyboard path) — with
    //     `_slotSplitIntent` armed, the mousedown call consumed the intent
    //     and landed the route in the second pane, then the click call re-ran
    //     with the intent cleared and wrote the SAME route into the PRIMARY:
    //     one click changed both panes (the reported bug). Now the second
    //     call resolves to the second pane and dedupes to a no-op.
    if (paneKey === '_canvasSlotSecond') {
      root.update({ _canvasSlotSecond: route })
      return
    }
    if (
      root._canvasSlotSecond &&
      shellCleanHref(root._canvasSlotSecond) === shellCleanHref(route)
    ) {
      root.update({ _canvasSlotSecond: route })
      return
    }
    root.update({ _canvasSlot: route })
    return
  }
  return this.router(route, this.getRoot())
}

// Select an app FROM THE APPS SWITCHER (springboard tile). Beyond plain
// openAppPage routing, this checkpoints the in-progress Simone conversation
// into history and surfaces the chat-history list, so switching app context
// never loses the current thread and the user can resume/branch it in the new
// surface (2026-06-14 per Nika "save thread and open chat history when we
// select an app"). Distinct from openAppPage so SIMONE's own navigate_to_app
// tool (and the /add-app button) keep plain routing — only a deliberate
// user app-pick from the switcher saves + opens history.
// el.call('selectAppFromSwitcher', route)
export function selectAppFromSwitcher(route, opts) {
  if (!route) return
  const root = this?.state?.root || this?.state
  // Save thread: snapshot the live Simone buffer into root.sessions — but only
  // when there's something to save, so empty "New chat" threads don't pile up
  // in history. persistActiveSession is a no-op without an active session.
  try {
    if ((root?.chatMessages || []).length) this.call('persistActiveSession')
  } catch (_) {
    /* simone session fns absent off the workspace shell — ignore */
  }
  // Switching apps no longer auto-opens the assistant drawer / chat history —
  // that fired on every PageSlot open / page switch and was disruptive. The
  // thread is still saved above, so it stays resumable when the user opens the
  // assistant deliberately (the magic-star toggle). (2026-06-22 per Nika "fix
  // why opening PageSlot or switching page triggers assistant opening".)
  // preferSlot: a pick from the app switcher is the same *kind* of gesture as a
  // navbar tab click, so it gets the same container. Without this the two
  // disagreed for any route outside the static APP_WINDOW_ROUTES set — an
  // installed module's tab ('/crm', '/cms') opened in the PageSlot from the
  // navbar and as a FULL PAGE from the search dropdown, for the same app.
  //
  // The intent has been on record since 2026-07-14 ("all non-canvas pages from
  // search dropdown should open in PageSlot"); it was implemented by listing
  // routes in APP_WINDOW_ROUTES, which cannot cover module routes that only
  // exist once an app is installed. Declaring the intent is what generalizes.
  // Restated 2026-08-09: "opening appstore and mostly all non-canvas pages
  // should open in pageslot from / page, search dropdown and navbar".
  //
  // Safe because preferSlot only ever routes NON-canvas links FROM HERE:
  // isCanvasRoute and isFullPageRoute both return ahead of it, so canvas
  // surfaces and the /admin + /data settings pages (which carry their own
  // sidebar nav) still take the full page. The D2 §8.2 pane allowance for
  // /tasks + /files needs an explicit split gesture or an origin INSIDE an
  // open pane. The switcher is outside every pane, but preserves an explicit
  // split gesture in opts. openAppPage remains the one intent normalizer.
  this.call('openAppPage', route, { ...opts, preferSlot: true })
}

// Hover-close debounce (module-level, mirrors the More-menu pattern in
// shared/components/AppNavbar.js): the search opens the panel on hover
// too, so leaving the pill arms a 200ms close that the panel's own
// mouseenter cancels — the cursor can travel from the pill into the
// grid without it snapping shut.
let _searchAppsCloseTimer = null

// A dismissal STAMP. The dropdown plugin restores focus to the closing
// layer's restoreEl, and for this launcher that target is the search INPUT
// (A2-08 — anything else drops the user's keystrokes). The input's own
// onFocus opens the launcher, so every close handed focus back to the one
// element that re-opened it: Escape, an outside click and a tile pick all
// looked like they did nothing (measured live — layerId stayed 'searchApps'
// through an Escape, with focus on the Input).
//
// SearchAppsMenu.onRemove stamps here as the body unmounts, which is inside
// closeDropdown's own state write and therefore BEFORE its focus restore.
// The input's onFocus reads the stamp and lets that one focus pass without
// opening anything. Deliberate opens (the chevron, Cmd+K, a click into the
// field) are unaffected — they arrive well outside the window.
let _searchAppsDismissedAt = 0
const SEARCH_APPS_REOPEN_GRACE_MS = 400

export function noteSearchAppsDismissed() {
  _searchAppsDismissedAt = Date.now()
}

export function searchAppsJustDismissed() {
  return Date.now() - _searchAppsDismissedAt < SEARCH_APPS_REOPEN_GRACE_MS
}

// A DELIBERATE open request that lands inside the grace window clears the
// stamp first. The stamp exists for one case — the focus a close hands back
// to the search input — and a pane's Split button is not that case: its press
// is outside the open launcher, so the dropdown's own outside-press close
// stamps a dismissal a moment BEFORE the button asks the search to open for
// the split pick (canvas/components/PageSlot.js SplitBtn). Without this the
// input's focus read that fresh stamp as "the close's own restore" and opened
// nothing: the search took focus with no dropdown under it.
export function forgetSearchAppsDismissed() {
  _searchAppsDismissedAt = 0
}

export function cancelSearchAppsClose() {
  if (_searchAppsCloseTimer) {
    clearTimeout(_searchAppsCloseTimer)
    _searchAppsCloseTimer = null
  }
}

export function scheduleSearchAppsClose() {
  const el = this
  cancelSearchAppsClose()
  // Dismiss-inert is CANVAS-SURFACE-ONLY — that is its whole reason to
  // exist: a drag started during the ~200ms fade must reach the pan/zoom
  // board underneath instead of being swallowed by the dismissing panel
  // (the July /studio drag bug, smbls 834d6ba23 + workspace 1c483192).
  // Arming it EVERYWHERE was the "search dropdown doesn't open / flickers
  // when the PageSlot is open" bug (Nika 2026-08-06): pointer-events:none
  // also kills the panel's OWN mouseenter, so the documented re-enter
  // cancel could never fire — and the focused-skip below kept the panel
  // OPEN but permanently click-through: an open panel where no tile click
  // lands, every click passing through to whatever sits beneath (over an
  // open slot sheet, very visibly so). Non-canvas surfaces keep a solid
  // panel through the debounce; re-entering it cancels the close again.
  const _onCanvasSurface =
    typeof window !== 'undefined' && isCanvasRoute(window.location.pathname || '')
  if (_onCanvasSurface) el.call('setDropdownInert', true)
  _searchAppsCloseTimer = setTimeout(() => {
    _searchAppsCloseTimer = null
    // Keep the panel open as long as the search input is still focused
    // (2026-07-14 per Nika "fix dropdown that hides on mouseout — keep it as
    // long as search is focused"). mouseleave off the panel/pill fires this
    // close, but while the user is focused/typing the panel must stay — only
    // a real blur (focus left the pill) or the dropdown plugin's outside-click
    // should close it. A HOVER-opened panel (input not focused) still closes
    // on mouseout, unchanged.
    if (typeof document !== 'undefined') {
      const active = document.activeElement
      // A2-06 — focus INSIDE the launcher counts exactly as much as focus in
      // the pill. Arrowing or tabbing from the field into a tile blurs the
      // input, which is what schedules this close; without this clause the
      // panel dismissed itself the moment a keyboard user reached it, so it
      // could never be operated from the keyboard at all. The panel is matched
      // by the PLUGIN's own marker: the layer body mounts through `el.set`, so
      // it carries the key `content`, never the component's own name.
      // A menu of the SEARCH (a launcher tile's or a result row's — opened
      // with `fromNavSearch`) takes focus and sits over the panel, so both the
      // input's blur and the panel's mouseleave schedule this close while it
      // is up: the launcher stays behind its own menu. Every close of that
      // menu nulls `contextDropdown`, and the next leave closes as before.
      const menu = el && el.state && el.state.root && el.state.root.contextDropdown
      const inside =
        (active &&
          active.closest &&
          (active.closest('[data-key="HeaderSearch"]') ||
            active.closest('[data-smbls-dropdown="panel"]'))) ||
        !!(menu && menu.open && menu.fromNavSearch)
      if (inside) {
        // Close skipped — the panel lives on and MUST be interactive again.
        // Leaving the dismiss-inert armed here was the permanent
        // click-through state described above.
        el.call('setDropdownInert', false)
        return
      }
      // Focus HAS left the pill and the panel, so this close is a consequence
      // of the user moving on — falls through to the close below. Used to
      // also retarget the plugin's restore here (`keepDropdownFocus`); no
      // shell-side retarget is needed any more.
      // @symbo.ls/dropdown's closeDropdown now makes this check itself
      // (FW-DROPDOWN-CLOSE-RESTORES-FOCUS-UNCONDITIONALLY-1): it restores
      // focus only when the close would otherwise lose it — <body>, or
      // still inside the closing panel — and leaves it alone here, where
      // `active` is a real control outside both.
    }
    // Stamp the dismissal before the close — see noteSearchAppsDismissed.
    noteSearchAppsDismissed()
    el.call('closeDropdown', 'searchApps')
  }, 200)
}

// Open the apps dropdown anchored to the global search bar. The header
// search asks for it through ensureSearchLauncher (below) on focus and on
// every keystroke (9A: it stays open while the person types); the HeaderTabs
// chevron and the home App strip open it directly.
// The panel spans the FULL width of the search pill (per Nika) —
// measured off the anchor at open time so it tracks the responsive
// search width — and paints the same `document` surface the dashboard
// cards (and the search pill itself) use.
// el.call('openSearchAppsDropdown', { anchorEl, restoreEl })
//
// `restoreEl` (A2-08) — the a11y focus-restore target of the layer, kept
// SEPARATE from `anchorEl`. The panel is anchored (and width-measured) on the
// whole search PILL, but the thing the user is actually on is the search
// INPUT inside it. Historically (pre FW-DROPDOWN-CLOSE-RESTORES-FOCUS-
// UNCONDITIONALLY-1) @symbo.ls/dropdown's closeDropdown re-focused
// `layer.restoreEl` (then named `triggerEl`) UNCONDITIONALLY on every close,
// and openDropdown defaults that to the anchor node — so the first keystroke
// (which closed this launcher until 9A kept it open) moved
// focus off the input and onto the pill's own `<search>` element (focusable:
// SearchBar extends DropdownParentFocus → tabindex 0), and every later
// keystroke went to a non-input, so the field kept only the first character.
// closeDropdown now restores focus ONLY when the close would otherwise lose
// it, so this override is no longer the only thing standing between typing
// and the bug — but it is still what makes Escape / outside-click / a tile
// pick land the restore on the input rather than the (also-focusable) pill,
// so it stays explicit rather than re-derived from the anchor.
export function openSearchAppsDropdown(opts) {
  cancelSearchAppsClose()
  // Fresh open — a brand-new dropdown layer starts pointer-interactive
  // (openDropdown never carries a stale `inert`), but clear defensively in
  // case a previous dismiss left the same layer inert mid-close.
  this.call('setDropdownInert', false)
  const anchorEl = opts?.anchorEl || this
  const root = this?.state?.root || this?.state
  const dropdownRoot = this.getRoot?.()?.DropdownRoot
  const splitPicker = !!root?._slotSplitIntent
  const railLauncher = !!opts?.anchorRect && /^(left|right)/.test(opts?.placement || '')
  // The search model of this open (`_launcherMode`, launcherTileGroups):
  // only the header search's own path (ensureSearchLauncher) asks for the
  // companion; every other opener — the navbar chevron, the home App strip —
  // is a trigger, and a trigger's own filter starts empty: a word typed in the
  // last panel must not hide tiles in this one.
  const launcherMode = opts?.launcherMode === 'companion' ? 'companion' : 'trigger'
  if (typeof root?.update === 'function') {
    root.update(
      launcherMode === 'trigger'
        ? { _launcherMode: launcherMode, _launcherFilter: '' }
        : { _launcherMode: launcherMode },
      { preventFetch: true }
    )
  }
  this.call('openDropdown', {
    id: 'searchApps',
    // A navbar rail's chevron hands a RECT and a side instead (shared
    // functions/navbarLayout.js navbarMenuAnchor): the rail's outer edge at
    // the chevron's height, the panel beside the rail.
    ...(opts?.anchorRect ? { anchorRect: opts.anchorRect } : { anchorEl }),
    restoreEl: opts?.restoreEl || anchorEl,
    onClose: () => {
      if (!splitPicker) return
      // App rows close the dropdown before opening their destination in
      // the same event. Let that selection consume the intent first; a
      // dismissal without a selection clears the empty split pane.
      queueMicrotask(() => {
        // Revealing/focusing the header input replaces this same launcher.
        // The picker is still open; its previous layer is not a dismissal.
        if (dropdownRoot?.state?.layer?.id === 'searchApps') return
        // Typing hands the same picker to the search results. Keep its
        // destination until a result is picked or the search is dismissed.
        if (root?._navSearchOpen) return
        if (root?._slotSplitIntent) root.update({ _slotSplitIntent: false })
      })
    },
    // Drop the panel DOWN from the navbar search pill (anchored just below it)
    // with the directional slide-in animation, instead of the viewport-centered
    // spotlight (2026-06-18 per Nika "should be down to the navbar, open with
    // animation" — reverses the 2026-06-17 'center' placement). 'bottom-center'
    // keeps the wide panel horizontally centered under the (centered) pill while
    // resolving side='bottom' → fadeInTransform translateY(-6px) slide.
    // Beside a rail: the side the opener names ('right-end', …).
    placement: opts?.placement || 'bottom-center',
    // As wide as the body asks (9A, Nika 2026-09-28: "make it more wider and
    // add maxWidth also"): SearchAppsMenu declares `width: 'overlayXl'`, the
    // brand's wide overlay, and the positioned panel shrink-wraps it. Only
    // the VIEWPORT bound lives here — a max, never a min: the old fixed
    // min = max pair (760 px) kept the panel from ever being the token's
    // width, and a min that exceeds the max would win and overflow.
    // The launcher's WIDTH is a token on its body; the only numbers here are
    // the viewport bounds the positioned panel may never pass (32px gutter).
    // Keep a rail launcher beside the rail even when the wide body cannot
    // fit at its preferred size. The plugin leaves an 8px gap at each edge.
    maxWidth:
      opts?.anchorRect && opts?.placement?.startsWith('right')
        ? `calc(100dvw - ${opts.anchorRect.right}px - 16px)`
        : opts?.anchorRect && opts?.placement?.startsWith('left')
          ? `${Math.max(0, opts.anchorRect.left - 16)}px`
          : 'calc(100dvw - 32px)',
    maxHeight: railLauncher ? 'calc(100dvh - 16px)' : 'calc(100dvh - 32px)',
    // THE GLASS SITS ON THE LAYER — the popover recipe (DESIGN_SPEC 6.1: the
    // shell glass on the overlay elevation, the card radius), the same opt-in
    // NotificationBell and AiSettingsButton pass. The body (SearchAppsMenu)
    // paints no surface of its own.
    // Why not on the body (it was, until the live check of 3efc1eace on dev,
    // 2026-09-30): the plugin Panel is a BACKDROP ROOT for as long as it is
    // open — @symbo.ls/dropdown writes `opacity: 0.999` on it, and its open
    // keyframe keeps `animation-fill-mode: both` on opacity, which acts as
    // `will-change: opacity`. A backdrop-filter INSIDE a backdrop root samples
    // only that root's own surface, and the Panel's was empty: the page text
    // showed sharp through the .55 fill (111 under the glass = the fill alone;
    // luminance spread 77.9 against 3.1 for a control outside the Panel). The
    // Panel's OWN backdrop-filter samples the page, at every opacity.
    // No `background` here: a surface override beats the theme's fill (the
    // plugin paints `--smbls-dropdown-surface` with !important).
    theme: railLauncher ? 'common-box' : 'search-glass',
    shadow: 'overlay',
    // The card radius, the one the body's own rounded clip carries too
    // (SearchAppsMenu: MenuPanel's radiusCard) — a mismatched shell radius
    // clips the corners under the panel's overflow (the corner-peek class the
    // 2026-09-02 dropdown-surface audit fixed on appOrgSwitcher/
    // langSwitcher; this call was its flagged ws-side sibling).
    round: 'radiusCard',
    content: {
      extends: 'SearchAppsMenu',
      railLauncher
    }
  })
}

// Is the launcher the open dropdown layer right now? Read off the shell's
// dropdown root — the layer the plugin owns — never a copy of it.
export function searchLauncherIsOpen() {
  const layer = this?.getRoot?.()?.DropdownRoot?.state?.layer
  return !!(layer && layer.id === 'searchApps')
}

// THE ONE SEARCH DROPDOWN (9A — DESIGN-WL-PAGESLOT-OPEN-SPLIT-SEARCH-1): the
// launcher stays open while the person types and hosts the results itself
// (components/SearchAppsMenu.js SamResults). The header search asks for it on
// focus and on every keystroke (shared HeaderSearch, the design-d2 hunk);
// this opens it only when it is NOT already the open layer — a keystroke must
// never re-mount the panel (the plugin restarts its entrance on every open).
// It opens the COMPANION (`_launcherMode`). A launcher a trigger opened — the
// navbar chevron, the home App strip — is re-opened as the companion once:
// its tiles answer the panel's own field, and the header's typing needs the
// results view under the header. Returns true when it opened.
// el.call('ensureSearchLauncher', { anchorEl, restoreEl })
export function ensureSearchLauncher(opts) {
  if (this.call('searchLauncherIsOpen')) {
    const root = this?.state?.root || this?.state
    if (root?._launcherMode !== 'trigger') return false
  }
  this.call('openSearchAppsDropdown', { ...opts, launcherMode: 'companion' })
  return true
}

// Close the launcher when the search itself ends — a picked result, a
// command, Escape, the search's own close. The dismissal is stamped first
// (see noteSearchAppsDismissed): the close hands focus back to the search
// input, whose focus would otherwise open the launcher again.
export function closeSearchLauncher() {
  if (!this.call('searchLauncherIsOpen')) return false
  this.call('noteSearchAppsDismissed')
  this.call('closeDropdown', 'searchApps')
  return true
}

// A TRIGGER'S SECOND PRESS closes the launcher it opened — the popup
// trigger's toggle half. The plugin leaves that press to the trigger: its
// outside-press close skips a press inside the anchor ("the anchor's own
// click is the trigger's job", @symbo.ls/dropdown DropdownRoot), so an opener
// that only ever opens kept the panel up under a second press (live on dev
// 286b17bdf, 2026-10-29: the home strip's "All apps" pressed twice, the 880px
// launcher still up after 1 s). The open layer keeps its opener as
// `restoreEl` (the node openDropdown resolved), so the opener is read off the
// layer. A launcher another control opened — the header search's companion,
// the other strip's "All apps" — is replaced, as an open always did.
// Its callers: the home App strip's "All apps" and the navbar chevron
// (shared HeaderTabs). Returns true when it opened.
// el.call('toggleSearchAppsDropdown', { anchorEl, restoreEl })
export function toggleSearchAppsDropdown(opts) {
  const opener = opts?.restoreEl || opts?.anchorEl || this
  const node = opener?.node || opener
  const layer = this?.getRoot?.()?.DropdownRoot?.state?.layer
  if (layer && layer.id === 'searchApps' && node && layer.restoreEl === node) {
    this.call('closeSearchLauncher')
    return false
  }
  this.call('openSearchAppsDropdown', opts)
  return true
}

// Hand the keyboard to the OPEN launcher, at its first or its last row — the
// header search's Tab edge (shared AppHeader › HeaderSearch keepSearchTab;
// ledger WH-5, sam: with Ctrl+K open, Tab left the search for the hidden
// buttons behind it and the first tile took ~21 presses). The rows are the
// ones the launcher's own Tab cycle walks (components/SearchAppsMenu.js
// tabCycle), so the next Tab continues inside the launcher. Read off the
// shell's dropdown host, the layer the plugin owns. Returns whether a row took
// focus; a closed launcher takes nothing.
// el.call('focusSearchLauncher', 'first' | 'last')
export function focusSearchLauncher(edge) {
  if (!this.call('searchLauncherIsOpen')) return false
  const host = this?.getRoot?.()?.DropdownRoot?.node
  if (!host || typeof host.querySelectorAll !== 'function') return false
  const rows = [...host.querySelectorAll('[role="menuitem"], [data-launcher-category]')].filter(
    (n) =>
      !n.hasAttribute('disabled') &&
      typeof n.getClientRects === 'function' &&
      n.getClientRects().length > 0
  )
  const row = edge === 'last' ? rows[rows.length - 1] : rows[0]
  if (!row || typeof row.focus !== 'function') return false
  row.focus()
  return true
}

// Put the keyboard back in the header search — the ONE focus owner of the
// search (9A). The launcher's filter row is a mirror of that input, so a
// press on it lands the caret there. Walks the shell's element tree to the
// header's own input (AppHeader › HeaderSearch › Form › Input), never a DOM
// query. Returns whether the input took focus.
//
// The launcher can be open while the header search is CLOSED — the home App
// strip's "All apps" opens it from its own tile. The header's input then sits
// in the hidden morph, and focusing it would put the caret in a field nobody
// sees. So a closed search is OPENED instead, the header's own way (the
// SearchBtn's writes: remember where focus returns, then `_headerSearchOpen`),
// and the header focuses its input on that edge (AppHeader focusSearchOnOpen).
export function focusHeaderSearch() {
  const shell = this?.getRoot?.()
  const input = shell?.AppHeader?.HeaderSearch?.Form?.Input?.node
  if (!input || typeof input.focus !== 'function') return false
  const root = shell?.state?.root || shell?.state
  if (root && root._headerSearchOpen === false && typeof root.update === 'function') {
    const active = typeof document !== 'undefined' ? document.activeElement : null
    if (active && active !== document.body) root.__headerSearchReturnFocusTo = active
    root.update({ _headerSearchOpen: true })
    return true
  }
  input.focus()
  return typeof document === 'undefined' || document.activeElement === input
}
